"use client";

import posthog from "posthog-js";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AIChatActions, AIChatContextValue, AIChatState } from "@/components/ai-chat/contexts";
import { AnalyticsResultType } from "@/components/ai-chat/types";
import type { AIChatAPIFunctions, AnalyticsResultPayload, AnalyticsStepPayload, ChatMessage } from "@/components/ai-chat/types";
import { useChatFeedback } from "@/hooks/use-chat-feedback";
import { filesToBase64 } from "@/lib/file-to-base64";
interface UsePatientQAChatOptions {
  api: AIChatAPIFunctions;
  chatType?: string;
  modelName?: string;
  initialSuggestions?: string[];
  initialHistory?: ChatMessage[];
  onHistoryChange?: (history: ChatMessage[]) => void;
  triggerQuestion?: string;
}
export interface UsePatientQAChatReturn {
  state: AIChatState;
  actions: AIChatActions;
  suggestions: string[];
  isGeneratingSuggestions: boolean;
  suggestionKey: number;
  feedback: NonNullable<AIChatContextValue["feedback"]>;
  summaryText: string | null;
  showSummaryDialog: boolean;
  setShowSummaryDialog: (open: boolean) => void;
}

/** Convert conversation history to API format (filter and transform summaries) */
function prepareHistoryForAPI(history: ChatMessage[]) {
  return history.filter(msg => msg.role === "user" || msg.role === "assistant" || msg.isSummary === true).map(msg => {
    if (msg.isSummary === true || msg.role === "system") {
      return {
        role: "assistant" as const,
        content: `[SUMMARY]${msg.content}`,
        files: msg.files
      };
    }
    const base = {
      role: msg.role as "user" | "assistant",
      content: msg.content,
      files: msg.files
    };
    if (msg.role === "assistant" && msg.analyticsResult) {
      return {
        ...base,
        analyticsResult: msg.analyticsResult
      };
    }
    return base;
  });
}

/** Calculate hash from conversation history for change detection */
function calculateConversationHash(history: ChatMessage[]) {
  return history.map(msg => `${msg.role}:${msg.isSummary ? "summary" : ""}:${msg.content}:${msg.timestamp instanceof Date && !Number.isNaN(msg.timestamp.getTime()) ? msg.timestamp.getTime() : 0}`).join("|");
}

/**
 * Domain-specific hook for the Patient QA chat.
 * Extracts streaming, suggestion generation, and feedback logic
 * that was previously trapped inside the monolithic AIChat component.
 * Returns {state, actions} ready for AIChat.Provider.
 */
export function usePatientQAChat(options: UsePatientQAChatOptions): UsePatientQAChatReturn {
  const {
    api,
    chatType,
    modelName: defaultModelName = "gemini-3.1-flash-lite",
    initialSuggestions,
    initialHistory = [],
    onHistoryChange,
    triggerQuestion
  } = options;

  // ── Core state ──────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>(initialHistory);
  const [streamingText, setStreamingText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activityLabel, setActivityLabel] = useState<string | null>(null);
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // ── Feedback ────────────────────────────────────────────────────────
  const {
    handleFeedbackClick,
    showFeedbackDialog,
    setShowFeedbackDialog,
    feedbackText,
    setFeedbackText,
    handleFeedbackDialogSubmit
  } = useChatFeedback({
    messages,
    setMessages,
    chatType
  });

  // ── History change notification ─────────────────────────────────────
  const onHistoryChangeRef = useRef(onHistoryChange);
  useEffect(() => {
    onHistoryChangeRef.current = onHistoryChange;
  }, [onHistoryChange]);
  useEffect(() => {
    onHistoryChangeRef.current?.(messages);
  }, [messages]);

  // ── Streaming ───────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text: string, files?: File[]) => {
    const userQuestion = text.trim();
    if (!userQuestion && (!files || files.length === 0)) return;

    // Abort if already streaming
    if (isStreaming) {
      abortControllerRef.current?.abort();
      setIsStreaming(false);
      setStreamingText("");
      abortControllerRef.current = null;
    }

    // Track
    posthog.capture("ai_chat_question_asked", {
      chat_type: chatType,
      question_length: userQuestion.length,
      has_files: !!files?.length,
      files_count: files?.length ?? 0
    });
    setStreamingText("");
    setError(null);

    // Convert files to base64 attachments
    const fileAttachments = files && files.length > 0 ? await filesToBase64(files) : undefined;

    // Add user message
    const userMessage: ChatMessage = {
      role: "user",
      content: userQuestion || "Sent files",
      timestamp: new Date(),
      files: fileAttachments
    };
    setMessages(prev => [...prev, userMessage]);

    // Prepare history (prior messages only)
    const historyForAPI = prepareHistoryForAPI(messagesRef.current);
    setIsStreaming(true);
    const myController = new AbortController();
    abortControllerRef.current = myController;
    try {
      let fullText = "";
      let chatId: string | undefined;
      const accumulatedSteps: AnalyticsStepPayload[] = [];
      for await (const chunk of api.streamResponse(userQuestion || "Please analyze the attached files", historyForAPI.length > 0 ? historyForAPI : undefined, fileAttachments, myController.signal, defaultModelName)) {
        if (myController.signal.aborted) break;

        // Metadata (chat_id from backend)
        if (chunk && typeof chunk === "object" && "type" in chunk && (chunk as {
          type: string;
        }).type === "metadata" && "chat_id" in chunk) {
          chatId = String((chunk as {
            chat_id: unknown;
          }).chat_id);
          continue;
        }

        // Agent lookups
        if (chunk && typeof chunk === "object" && "type" in chunk && chunk.type === "tool_call" && "label" in chunk) {
          setActivityLabel(String(chunk.label));
          continue;
        }

        // Summary events
        if (chunk && typeof chunk === "object" && "type" in chunk && (chunk as {
          type: string;
        }).type === "summary" && "summary" in chunk) {
          const text = String((chunk as {
            summary: unknown;
          }).summary || "");
          setSummaryText(text);
          setMessages(prev => {
            const hasSummary = prev.some(msg => msg.isSummary === true || msg.role === "system");
            if (hasSummary) {
              return prev.map(msg => msg.isSummary === true || msg.role === "system" ? {
                ...msg,
                content: text,
                timestamp: new Date()
              } : msg);
            }
            return [...prev, {
              role: "system" as const,
              content: text,
              timestamp: new Date(),
              isSummary: true
            }];
          });
          continue;
        }

        // Analytics planning step
        if (chunk && typeof chunk === "object" && "type" in chunk && (chunk as {
          type: string;
        }).type === "analytics_step" && "step" in chunk) {
          const {
            step
          } = chunk as unknown as {
            step: AnalyticsStepPayload;
          };
          const idx = accumulatedSteps.findIndex(s => s.step === step.step);
          if (idx >= 0) {
            accumulatedSteps[idx] = {
              ...accumulatedSteps[idx],
              ...step
            };
          } else {
            accumulatedSteps.push(step);
          }
          continue;
        }

        // Analytics result
        if (chunk && typeof chunk === "object" && "type" in chunk && (chunk as {
          type: string;
        }).type === "analytics_result" && "result" in chunk) {
          const {
            result
          } = chunk as unknown as {
            result: AnalyticsResultPayload;
          };
          const content = result.result_type === AnalyticsResultType.MESSAGE || result.result_type === AnalyticsResultType.QUESTION ? result.message || result.explanation : result.narrative ?? "";
          const assistantMessage: ChatMessage = {
            role: "assistant",
            content: content || "",
            timestamp: new Date(),
            feedbackType: null,
            id: chatId,
            analyticsResult: result,
            analyticsSteps: [...accumulatedSteps]
          };
          setMessages(prev => [...prev, assistantMessage]);
          setStreamingText("");
          continue;
        }

        // Text chunks
        if (typeof chunk === "string") {
          fullText += chunk;
          setStreamingText(fullText);
        }
      }
      if (!abortControllerRef.current?.signal.aborted && fullText) {
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: fullText,
          timestamp: new Date(),
          feedbackType: null,
          id: chatId
        };
        setMessages(prev_0 => [...prev_0, assistantMessage]);
        setStreamingText("");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      if (abortControllerRef.current === myController) {
        setIsStreaming(false);
        setActivityLabel(null);
        abortControllerRef.current = null;
      }
    }
  }, [api, chatType, defaultModelName, isStreaming]);
  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
    setStreamingText("");
  }, []);
  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setStreamingText("");
    setIsStreaming(false);
    setError(null);
  }, []);

  // ── Auto-send triggerQuestion ───────────────────────────────────────
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);
  const lastTriggerRef = useRef<string | null>(null);
  useEffect(() => {
    const q = triggerQuestion?.trim();
    if (q && q !== lastTriggerRef.current && !isStreaming) {
      lastTriggerRef.current = q;
      void sendMessageRef.current(q);
    }
  }, [triggerQuestion, isStreaming]);

  // ── Suggestion generation ───────────────────────────────────────────
  const [dynamicSuggestions, setDynamicSuggestions] = useState<string[] | null>(null);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [suggestionKey, setSuggestionKey] = useState(0);
  const lastConversationHashRef = useRef("");
  const suggestionsAbortRef = useRef<AbortController | null>(null);
  const dynamicSuggestionsRef = useRef<string[] | null>(null);
  useEffect(() => {
    dynamicSuggestionsRef.current = dynamicSuggestions;
  }, [dynamicSuggestions]);
  const generateSuggestionsFn = api.generateSuggestions;
  useEffect(() => {
    const conversationHash = calculateConversationHash(messages);
    if (conversationHash === lastConversationHashRef.current) return;

    // Cancel ongoing
    suggestionsAbortRef.current?.abort();
    suggestionsAbortRef.current = null;
    if (messages.length === 0) {
      lastConversationHashRef.current = "";
      setIsGeneratingSuggestions(false);
      setDynamicSuggestions(null);
      return;
    }

    // Skip when LLM asks a clarifying question
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.role === "assistant" && lastMsg?.analyticsResult?.result_type === AnalyticsResultType.QUESTION) {
      lastConversationHashRef.current = conversationHash;
      setIsGeneratingSuggestions(false);
      setDynamicSuggestions(null);
      return;
    }
    if (!generateSuggestionsFn) return;
    const timeoutId = setTimeout(() => {
      const hashForGeneration = calculateConversationHash(messages);
      if (hashForGeneration !== lastConversationHashRef.current) {
        lastConversationHashRef.current = hashForGeneration;
      }
      const abortController = new AbortController();
      suggestionsAbortRef.current = abortController;
      setDynamicSuggestions(null);
      setIsGeneratingSuggestions(true);
      const historyForAPI_0 = prepareHistoryForAPI(messages).map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      (async () => {
        try {
          if (abortController.signal.aborted) return;
          const newSuggestions = await generateSuggestionsFn(historyForAPI_0);
          if (abortController.signal.aborted) return;
          const currentHash = calculateConversationHash(messagesRef.current);
          if (currentHash === hashForGeneration) {
            setDynamicSuggestions([...newSuggestions]);
            setSuggestionKey(prev_1 => prev_1 + 1);
          }
          setIsGeneratingSuggestions(false);
        } catch (err_0) {
          if (abortController.signal.aborted) return;
          if (process.env.NODE_ENV === "development") {
            console.error("Error generating suggestions:", err_0);
          }
          setIsGeneratingSuggestions(false);
          setDynamicSuggestions(null);
          lastConversationHashRef.current = "";
        } finally {
          if (suggestionsAbortRef.current === abortController) {
            suggestionsAbortRef.current = null;
          }
        }
      })();
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [messages, generateSuggestionsFn]);

  // Don't show suggestions during clarifying questions
  const lastMsg_0 = messages[messages.length - 1];
  const lastIsClarifying = lastMsg_0?.role === "assistant" && lastMsg_0?.analyticsResult?.result_type === AnalyticsResultType.QUESTION;
  const suggestions = lastIsClarifying ? [] : dynamicSuggestions !== null ? dynamicSuggestions : initialSuggestions && initialSuggestions.length > 0 ? initialSuggestions : [];

  // ── Return ──────────────────────────────────────────────────────────
  return {
    state: {
      messages,
      streamingText,
      isStreaming,
      error,
      activityLabel
    },
    actions: {
      sendMessage,
      cancel,
      reset,
      handleFeedback: handleFeedbackClick as AIChatActions["handleFeedback"]
    },
    suggestions,
    isGeneratingSuggestions,
    suggestionKey,
    feedback: {
      state: {
        isOpen: showFeedbackDialog,
        feedbackText
      },
      actions: {
        openFeedbackDialog: () => setShowFeedbackDialog(true),
        closeFeedbackDialog: () => {
          setShowFeedbackDialog(false);
          setFeedbackText("");
        },
        setFeedbackText,
        submitFeedback: handleFeedbackDialogSubmit
      }
    },
    summaryText,
    showSummaryDialog,
    setShowSummaryDialog
  };
}